import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';
import { formatDistanceToNow, format } from 'date-fns';
import { id } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { Users, FileText, CheckCircle, Clock, Settings, RefreshCw, Edit3, Eye, BarChart2, X, ChevronRight } from 'lucide-react';
import StatusBadge from '../../components/StatusBadge';
import ArticleStats from '../../components/ArticleStats';
import './AdminDashboard.css';

function DetailModal({ person, type, onClose }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/articles/admin/all?limit=200')
      .then(res => {
        const all = res.data.articles || [];
        const filtered = type === 'editor'
          ? all.filter(a => a.assignedEditor?._id === person._id || a.assignedEditor === person._id)
          : all.filter(a => a.reviewedByProducer === person._id || a.reviewedByProducer?._id === person._id);
        setArticles(filtered);
      })
      .catch(() => toast.error('Gagal memuat artikel'))
      .finally(() => setLoading(false));
  }, [person._id, type]);

  const STATUS_LABEL = {
    payment_pending: 'Menunggu Pembayaran', payment_confirmed: 'Pembayaran Dikonfirmasi',
    writing: 'Sedang Ditulis', review_producer: 'Di Producer', revision_editor: 'Revisi Editor',
    review_reader: 'Di Reader', revision_reader: 'Revisi Reader',
    approved: 'Disetujui', published: 'Terpublikasi',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--ki-card)', borderRadius: 14, width: '100%', maxWidth: 700, maxHeight: '80vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--ki-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--ki-border)' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {type === 'editor' ? 'Artikel Editor' : 'Artikel Producer'}: {person.name}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ki-text-faint)', marginTop: 2 }}>{person.email}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ki-text-muted)', padding: 4 }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '16px 24px' }}>
          {loading ? (
            [...Array(4)].map((_, i) => <div key={i} className="shimmer" style={{ height: 44, borderRadius: 8, marginBottom: 8 }} />)
          ) : articles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ki-text-faint)', fontSize: 14 }}>
              Belum ada artikel tercatat
            </div>
          ) : (
            <table className="ki-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Judul / Topik</th>
                  <th>Status</th>
                  <th>Terakhir Update</th>
                </tr>
              </thead>
              <tbody>
                {articles.map(a => (
                  <tr key={a._id}>
                    <td style={{ fontWeight: 600, fontSize: 13, maxWidth: 280 }}>{a.title || a.what}</td>
                    <td><StatusBadge status={a.status} /></td>
                    <td style={{ fontSize: 12, color: 'var(--ki-text-faint)', whiteSpace: 'nowrap' }}>
                      {formatDistanceToNow(new Date(a.updatedAt), { addSuffix: true, locale: id })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ padding: '12px 24px', borderTop: '1px solid var(--ki-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--ki-text-faint)' }}>{articles.length} artikel</span>
          <button onClick={onClose} className="btn btn-ghost btn-sm">Tutup</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [userCount, setUserCount] = useState(0);
  const [recentArticles, setRecentArticles] = useState([]);
  const [topArticles, setTopArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // { person, type }

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    const [statsRes, usersRes, articlesRes, topRes] = await Promise.allSettled([
      API.get('/articles/admin/stats'),
      API.get('/users'),
      API.get('/articles/admin/all?limit=8'),
      API.get('/articles/admin/all?limit=200'),
    ]);

    if (statsRes.status === 'fulfilled') {
      setStats({
        ...statsRes.value.data.stats,
        editorStats: statsRes.value.data.editorStats || [],
        producerStats: statsRes.value.data.producerStats || [],
      });
    } else if (articlesRes.status === 'fulfilled') {
      const all = articlesRes.value.data.articles || [];
      setStats({
        total: all.length,
        published: all.filter(a => a.status === 'published').length,
        inProgress: all.filter(a => ['writing','revision_editor','revision_reader'].includes(a.status)).length,
        atProducer: all.filter(a => a.status === 'review_producer').length,
        atReader: all.filter(a => ['review_reader','revision_reader'].includes(a.status)).length,
        approved: all.filter(a => a.status === 'approved').length,
        editorStats: [], producerStats: [],
      });
    }

    if (usersRes.status === 'fulfilled') {
      setUserCount((usersRes.value.data.users || usersRes.value.data.data || []).length);
    }
    if (articlesRes.status === 'fulfilled') {
      setRecentArticles(articlesRes.value.data.articles || []);
    }
    if (topRes.status === 'fulfilled') {
      const published = (topRes.value.data.articles || []).filter(a => a.status === 'published');
      setTopArticles(published.sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0)).slice(0, 20));
    }
    setLoading(false);
  };

  const s = stats || {};

  const statCards = [
    { label: 'Total Pengguna',  val: userCount,    icon: <Users size={20} />,       color: 'var(--ki-text)', bg: 'rgba(0,21,64,0.08)' },
    { label: 'Total Artikel',   val: s.total,      icon: <FileText size={20} />,    color: 'var(--ki-text)', bg: 'rgba(0,21,64,0.08)' },
    { label: 'Terpublikasi',    val: s.published,  icon: <CheckCircle size={20} />, color: 'var(--ki-text)', bg: 'rgba(212,105,10,0.10)' },
    { label: 'Dalam Proses',    val: s.inProgress, icon: <Clock size={20} />,       color: 'var(--ki-text)', bg: 'rgba(0,21,64,0.08)' },
    { label: 'Di Producer',     val: s.atProducer, icon: <Eye size={20} />,         color: 'var(--ki-text)', bg: 'rgba(0,21,64,0.08)' },
    { label: 'Di Reader',       val: s.atReader,   icon: <BarChart2 size={20} />,   color: 'var(--ki-text)', bg: 'rgba(0,21,64,0.08)' },
  ];

  return (
    <DashboardLayout title="Panel Admin" subtitle="Pantau seluruh aktivitas platform secara real-time">
      {detail && <DetailModal person={detail.person} type={detail.type} onClose={() => setDetail(null)} />}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <button className="btn btn-ghost btn-sm" onClick={fetchData}><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Stat cards */}
      <div className="stats-row">
        {statCards.map(sc => (
          <div key={sc.label} className="stat-card">
            <div className="stat-card-glow" style={{ background: sc.bg }} />
            <div style={{ color: sc.color }}>{sc.icon}</div>
            <div className="stat-val">{loading ? '...' : (sc.val ?? 0)}</div>
            <div className="stat-label">{sc.label}</div>
          </div>
        ))}
      </div>

      {/* Quick links */}
      <div className="admin-quicklinks" style={{ display: 'grid', gap: 16, marginBottom: 28 }}>
        {[
          { to: '/admin/users',      icon: <Users size={22} />,    label: 'Kelola Pengguna',  desc: 'Tambah, edit, ubah role, nonaktifkan akun', color: 'var(--ki-text)' },
          { to: '/admin/articles',   icon: <FileText size={22} />, label: 'Kelola Artikel',   desc: 'Pantau & assign semua artikel di platform',  color: 'var(--ki-text)' },
          { to: '/admin/categories', icon: <Settings size={22} />, label: 'Kelola Kategori',  desc: 'Atur topik dan kategori berita',             color: 'var(--ki-text)' },
        ].map(q => (
          <Link key={q.to} to={q.to} className="ki-card"
            style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 18, textDecoration: 'none', transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = q.color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.transform = ''; }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: q.color, flexShrink: 0 }}>{q.icon}</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 3 }}>{q.label}</div>
              <div style={{ fontSize: 12, color: 'var(--ki-text-muted)' }}>{q.desc}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* Editor summary */}
      <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Edit3 size={15} /> Ringkasan Kinerja Editor
      </div>
      {loading ? (
        <div className="ki-card">{[...Array(3)].map((_, i) => <div key={i} className="shimmer" style={{ height: 44, borderRadius: 8, marginBottom: 8 }} />)}</div>
      ) : (s.editorStats || []).length === 0 ? (
        <div className="ki-card" style={{ textAlign: 'center', padding: '24px', color: 'var(--ki-text-faint)', fontSize: 13 }}>
          Belum ada data editor. Pastikan backend sudah direstart.
        </div>
      ) : (
        <div className="ki-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 28 }}>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead>
              <tr>
                <th>Editor</th>
                <th style={{ textAlign: 'center' }}>Total</th>
                <th style={{ textAlign: 'center' }}>Sedang Dikerjakan</th>
                <th style={{ textAlign: 'center' }}>Di Producer</th>
                <th style={{ textAlign: 'center' }}>Terpublikasi</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(s.editorStats || []).map((e, i) => (
                <tr key={i}>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{e.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--ki-text-faint)' }}>{e.email}</div>
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--ki-text)' }}>{e.total}</td>
                  <td style={{ textAlign: 'center', color: 'var(--ki-text)', fontWeight: 600 }}>{e.inProgress}</td>
                  <td style={{ textAlign: 'center', color: 'var(--ki-text)', fontWeight: 600 }}>{e.atProducer}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ fontWeight: 700, color: 'var(--ki-text)', fontSize: 15 }}>{e.published}</span>
                  </td>
                  <td>
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => setDetail({ person: { _id: e._id, name: e.name, email: e.email }, type: 'editor' })}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                      Detail <ChevronRight size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Producer summary */}
      <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Eye size={15} /> Ringkasan Kinerja Producer
      </div>
      {loading ? (
        <div className="ki-card">{[...Array(2)].map((_, i) => <div key={i} className="shimmer" style={{ height: 44, borderRadius: 8, marginBottom: 8 }} />)}</div>
      ) : (s.producerStats || []).length === 0 ? (
        <div className="ki-card" style={{ textAlign: 'center', padding: '24px', color: 'var(--ki-text-faint)', fontSize: 13 }}>
          Belum ada data producer. Pastikan backend sudah direstart.
        </div>
      ) : (
        <div className="ki-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 28 }}>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead>
              <tr>
                <th>Producer</th>
                <th style={{ textAlign: 'center' }}>Total Direview</th>
                <th style={{ textAlign: 'center' }}>Disetujui ke Reader</th>
                <th style={{ textAlign: 'center' }}>Dikembalikan ke Editor</th>
                <th style={{ textAlign: 'center' }}>Terpublikasi</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(s.producerStats || []).map((p, i) => (
                <tr key={i}>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--ki-text-faint)' }}>{p.email}</div>
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--ki-text)' }}>{p.total}</td>
                  <td style={{ textAlign: 'center', color: 'var(--ki-text)', fontWeight: 600 }}>{p.approved}</td>
                  <td style={{ textAlign: 'center', color: 'var(--ki-text)', fontWeight: 600 }}>{p.revised}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ fontWeight: 700, color: 'var(--ki-text)', fontSize: 15 }}>{p.published}</span>
                  </td>
                  <td>
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => setDetail({ person: { _id: p._id, name: p.name, email: p.email }, type: 'producer' })}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                      Detail <ChevronRight size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <ArticleStats
        articles={topArticles}
        totalViews={topArticles.reduce((s, a) => s + (a.viewCount || 0), 0)}
        avgViews={topArticles.length ? Math.round(topArticles.reduce((s, a) => s + (a.viewCount || 0), 0) / topArticles.length) : 0}
        label="Artikel Terpopuler"
        secondaryCol="assignedEditor"
        secondaryLabel="Editor"
      />

      {/* Recent articles */}
      <div className="section-title" style={{ marginBottom: 12 }}>Artikel Terbaru</div>
      {loading ? (
        <div className="ki-card">{[...Array(5)].map((_, i) => <div key={i} className="shimmer" style={{ height: 48, borderRadius: 8, marginBottom: 8 }} />)}</div>
      ) : (
        <div className="ki-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead><tr><th>Judul / Topik</th><th>Editor</th><th>Status</th><th>Terakhir Update</th></tr></thead>
            <tbody>
              {recentArticles.map(a => (
                <tr key={a._id}>
                  <td style={{ fontWeight: 600, fontSize: 14, maxWidth: 320 }}>{a.title || a.what}</td>
                  <td style={{ fontSize: 13, color: 'var(--ki-text-muted)' }}>{a.assignedEditor?.name || '-'}</td>
                  <td><StatusBadge status={a.status} /></td>
                  <td style={{ fontSize: 12, color: 'var(--ki-text-faint)' }}>
                    {formatDistanceToNow(new Date(a.updatedAt), { addSuffix: true, locale: id })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
